/**
 * ProgramBucketTabs — an exact twin of CourseBucketTabs for /learn/programs
 * (product rule: one design for Course Library and Learning Programs).
 * Buckets come from each program's per-learner `progress.bucket`.
 */
import React, { useMemo, useState } from 'react';
import ProgramCard, { type ProgramWithProgress } from './ProgramCard';

type BucketKey = 'in_progress' | 'upcoming' | 'completed';

const TABS: { key: BucketKey; label: string }[] = [
  { key: 'in_progress', label: 'In Progress' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
];

interface ProgramBucketTabsProps {
  programs: ProgramWithProgress[];
}

const ProgramBucketTabs: React.FC<ProgramBucketTabsProps> = ({ programs }) => {
  const buckets = useMemo(() => {
    const out: Record<BucketKey, ProgramWithProgress[]> = {
      in_progress: [],
      upcoming: [],
      completed: [],
    };
    programs.forEach((program) => {
      out[program.progress?.bucket ?? 'in_progress'].push(program);
    });
    return out;
  }, [programs]);

  const [activeTab, setActiveTab] = useState<BucketKey>(() => {
    if (buckets.in_progress.length > 0) return 'in_progress';
    if (buckets.upcoming.length > 0) return 'upcoming';
    return 'completed';
  });

  const visible = buckets[activeTab];
  const countFor = (key: BucketKey) => buckets[key].length;

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-border">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === key
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {label}
            {countFor(key) > 0 && (
              <span
                className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === key
                    ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                    : 'bg-neutral-100 dark:bg-neutral-800 text-text-secondary'
                }`}
              >
                {countFor(key)}
              </span>
            )}
            {activeTab === key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-text-secondary text-sm">
          No {TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} programs.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visible.map((program) => (
            <ProgramCard key={program.id} program={program} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgramBucketTabs;
